import type { HttpMethod } from '@blufin/types';
import type { ITelemetryAgent } from '../../TelemetryAgent/TelemetryAgent';

export type LoggerContextConfigMetricsApi = {
  uri: string; // The URI for the metrics-service endpoint, IE: https://api.rannetsperger.com
};

export enum LoggerType {
  APP = 'APP', // The log originates from within an app (eg: UI).
  AWS = 'AWS', // The log originates from within AWS (eg: Cognito preTokenGeneration scripts).
  SCHEDULED = 'SCHEDULED', // The log originates from within a scheduled handler (eg: EventBridge Scheduler-triggered Lambda).
  SERVICE = 'SERVICE' // The log originates from within a service (eg: Lambda).
}

export type LoggerContextConfig = {
  domainPath?: string; // eg: @blufin/auth-service
  environment?: string; // eg: prod or dev
  fqdn?: string; // eg: auth.blufin.org/<domainPath>
  httpMethod?: HttpMethod; // If apigw.customDomain is "test-api.builtbybert.com/dynamodb", this would be "domain"
  loggerType: LoggerType; // Logger type: APP, AWS, or SERVICE
  metricsApi?: LoggerContextConfigMetricsApi; // eg: auth.blufin.org
  package?: string;
  path?: string; // The endpoint path (eg: "/alpha/authenticateUser")
  requestId?: string;
  scheduleName?: string; // The specific scheduled task name (eg: "overdueTaskSweeper") — only set for LoggerType.SCHEDULED
  version?: string; // UUID for request tracking and log correlation
};

const _isLocalhost = (): boolean => {
  if (typeof globalThis === 'undefined') return false;
  const win = (globalThis as { window?: { location?: { hostname?: string } } }).window;
  const hostname = win?.location?.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

export class LoggerContext {
  /**
   * Module-level singleton agent registry used by `LoggerType.APP` flush delegation.
   * The agent is registered via `LoggerContext.setAgent(...)` when its React host mounts and
   * unregistered via `LoggerContext.clearAgent(...)` when that host tears down (eg: the app
   * unmounting its authenticated tree on logout), so a subsequent mount can register a fresh
   * agent. Tests use `LoggerContext._resetAgentForTests()` between cases.
   */
  private static _registeredAgent: ITelemetryAgent | null = null;

  private config: LoggerContextConfig | null = null;

  /**
   * Unregister the telemetry agent when its React host tears down (eg: the authenticated tree
   * unmounting on logout). Guarded: only clears when `agent` is the currently-registered
   * reference, so a newer agent already registered by a subsequent mount is never clobbered by a
   * late cleanup — a no-op otherwise. This is what lets a re-login register a fresh agent instead
   * of tripping the "already registered" guard in `setAgent`.
   * @param agent - The telemetry agent being torn down.
   */
  static clearAgent(agent: ITelemetryAgent): void {
    if (LoggerContext._registeredAgent === agent) LoggerContext._registeredAgent = null;
  }

  /** Returns the currently registered telemetry agent, or `null` if none. */
  static getAgent(): ITelemetryAgent | null {
    return LoggerContext._registeredAgent;
  }

  /**
   * Register the singleton telemetry agent at app boot.
   * - Same-reference calls are no-ops (idempotent).
   * - Different-reference calls throw, EXCEPT on localhost (where Vite HMR may rebuild the agent).
   * @param agent - The telemetry agent.
   */
  static setAgent(agent: ITelemetryAgent): void {
    if (LoggerContext._registeredAgent === agent) return;
    if (LoggerContext._registeredAgent !== null) {
      if (_isLocalhost()) {
        LoggerContext._registeredAgent = agent;
        return;
      }
      throw new Error(
        'TelemetryAgent is already registered. Replacing the agent is only permitted on localhost (for Vite HMR).'
      );
    }
    LoggerContext._registeredAgent = agent;
  }

  /** Test-only: clear the registered agent. Production code must never call this. */
  static _resetAgentForTests(): void {
    LoggerContext._registeredAgent = null;
  }

  /**
   * Initialize the logger context with the provided configuration.
   * @param config - The logger context configuration.
   */
  init(config: LoggerContextConfig) {
    if (!this.config) this.config = config;
  }

  /**
   * Retrieves the logger context configuration.
   */
  getConfig(): LoggerContextConfig | null {
    return this.config;
  }
}
